import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { ChannelRef, PersistedState, UiPrefs } from '../types'
import {
  addChannel,
  loadState,
  orderChannels,
  removeChannel,
  saveState,
  toggleFavorite,
} from '../lib/storage'

type AppStateValue = {
  /** Display order: favorites first, then the rest, both stable. */
  channels: ChannelRef[]
  ui: UiPrefs
  /** True when a slug is already open as a tab. */
  hasChannel: (slug: string) => boolean
  addTab: (slug: string, favorite?: boolean) => ChannelRef | null
  removeTab: (slug: string) => void
  toggleFavorite: (slug: string) => void
  updateUi: (patch: Partial<UiPrefs>) => void
}

const AppStateContext = createContext<AppStateValue | null>(null)

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PersistedState>(() => loadState())

  useEffect(() => {
    saveState(state)
  }, [state])

  const patch = useCallback((next: (prev: PersistedState) => PersistedState) => {
    setState(next)
  }, [])

  const hasChannel = useCallback(
    (slug: string) => state.channels.some((c) => c.slug === slug),
    [state.channels],
  )

  const addTab = useCallback(
    (slug: string, favorite = false) => {
      if (state.channels.some((c) => c.slug === slug)) return null
      const ref: ChannelRef = {
        slug,
        addedAt: new Date().toISOString(),
        favorite,
        builtIn: false,
      }
      patch((prev) => ({ ...prev, channels: [...prev.channels, ref] }))
      return ref
    },
    [state.channels, patch],
  )

  const removeTab = useCallback(
    (slug: string) => {
      patch((prev) => ({ ...prev, channels: removeChannel(prev.channels, slug) }))
    },
    [patch],
  )

  const toggleFav = useCallback(
    (slug: string) => {
      patch((prev) => ({ ...prev, channels: toggleFavorite(prev.channels, slug) }))
    },
    [patch],
  )

  const updateUi = useCallback(
    (uiPatch: Partial<UiPrefs>) => {
      patch((prev) => ({ ...prev, ui: { ...prev.ui, ...uiPatch } }))
    },
    [patch],
  )

  const value = useMemo<AppStateValue>(
    () => ({
      channels: orderChannels(state.channels),
      ui: state.ui,
      hasChannel,
      addTab,
      removeTab,
      toggleFavorite: toggleFav,
      updateUi,
    }),
    [state.channels, state.ui, hasChannel, addTab, removeTab, toggleFav, updateUi],
  )

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext)
  if (!ctx) throw new Error('useAppState must be used inside AppStateProvider')
  return ctx
}

export { addChannel }